/**
 * Finding provenance: was this conclusion MEASURED or INTERPRETED?
 *
 *   deterministic - follows reproducibly from measured inputs and fixed rules
 *   judgement     - depends on interpretation or an LLM classification
 *
 * DERIVED FROM tier-map.ts, NOT a second table. That file calls itself "the
 * AUTHORITATIVE classification of every production scoring tool" and already
 * encodes this exact distinction as PRD §3 routing rules:
 *
 *   tier1     definite right answer, parser, NO LLM on the score path
 *   tier2     measures what a live engine actually did (observed, the anchor)
 *   tier3     genuinely subjective, no parser resolves (stabilized LLM judgment)
 *   plumbing  supports scoring but is not itself scored
 *
 * A parallel mapping here would be a second source of truth that silently
 * drifts the first time a tool is re-tiered. Everything below reads TIER_MAP.
 *
 * "USES AN LLM" IS NOT THE TEST. The five parity tools all import an LLM
 * client, but only to write the `aiSummary` narrative; their scores come from
 * calculateScore-style parsers, which is why crawl is tier1 ("NO LLM on the
 * score path"). citation-check is the sharper case: an LLM produces the
 * RESPONSE, and a pure string/regex classifier decides whether the brand was
 * mentioned — `lower.includes(brandLower)`, an unknown-phrase regex, length
 * thresholds, and zero LLM calls inside the classifier. Observing what an
 * engine actually said is a measurement, so it is deterministic.
 *
 * MIXED TOOLS ARE CONSERVATIVELY JUDGEMENT. aeo, answer and reddit fuse
 * measured and judged signals (`embeddedTiers` includes tier3), and an
 * ActionItem carries only `source: ToolKey` — not which sub-signal produced
 * it. Since we cannot prove an action came from the measured half, we decline
 * to claim it did. This deliberately UNDER-claims certainty rather than
 * presenting an LLM-derived conclusion as measured fact.
 *
 * KNOWN LIMITATION: tool-level classification is the finest reliable
 * granularity available today. A future per-finding provenance field would let
 * a mixed tool emit both deterministic and judgement findings accurately. That
 * is a separate change and deliberately NOT attempted here.
 */

import { getClassification, type ScoringTier } from './tier-map'
import type { FindingType } from './finding-type-constants'

// Re-exported so existing importers keep one place to reach for, while the
// GSC path can import the constants module directly and avoid pulling
// tier-map -> tool-registry into a module that never classifies by tool.
export type { FindingType, GscCheckKey } from './finding-type-constants'
export { GSC_FINDING_TYPES } from './finding-type-constants'

/**
 * Tools with no TIER_MAP entry.
 *
 * Returning a FindingType here would silently label an unknown source
 * "deterministic", which is the one failure mode that matters: it would
 * present an unclassified conclusion as measured fact. Callers get
 * `undefined` and must decide explicitly.
 */
export type FindingTypeResult = FindingType | undefined

/**
 * Map one PRD scoring tier to provenance. Plumbing is not a finding source.
 *
 * Exported for direct test coverage: no tool in TIER_MAP currently has a
 * PRIMARY tier of tier3 — every judged tool reaches `judgement` through the
 * embeddedTiers rule instead — so this branch is unreachable via
 * classifyFindingType today and would otherwise be untested future-proofing.
 */
export function tierToFindingType(tier: ScoringTier): FindingTypeResult {
  switch (tier) {
    case 'tier1':
    case 'tier2':
      return 'deterministic'
    case 'tier3':
      return 'judgement'
    case 'plumbing':
      return undefined
  }
}

/**
 * Classify a tool key. Returns undefined for unknown or plumbing keys so an
 * unclassified source can never masquerade as measured.
 */
export function classifyFindingType(toolKey: string): FindingTypeResult {
  const entry = getClassification(toolKey)
  if (!entry) return undefined

  // Conservative mixed rule: any tier3 signal fused into the output makes the
  // whole tool's actions judgement, because the action cannot tell us which
  // sub-signal it came from.
  if (entry.embeddedTiers?.includes('tier3')) return 'judgement'

  return tierToFindingType(entry.tier)
}

/**
 * Legacy compatibility, centralized here rather than repeated in consumers.
 *
 * Records written before T5 carry no findingType. They are NOT rewritten and
 * NOT guessed: an absent field stays absent, and a caller that needs a value
 * asks for one explicitly. Consumers must handle `undefined` rather than
 * defaulting, so a historical record is never relabelled as measured.
 */
export function findingTypeOf(value: {
  findingType?: FindingType
}): FindingTypeResult {
  return value.findingType
}
